import { createElement, type ComponentType } from "react";
import { filling, type AnyViewName, type Route, type ViewAnswer } from "@nabla/api";
import { entrypoint, moduleViewOf, type Entrypoint, type ViewProps } from "../../client/index.ts";
import { catalog } from "../../modules.generated.ts";
import { later } from "../../modules.later.generated.ts";

interface Place<K extends AnyViewName> {
  readonly id: string;
  readonly path: string;
  readonly view: K;
  readonly route: Route<"GET">;
  readonly query: readonly string[];
  readonly title: string;
}
type Page<T> = () => Promise<{ readonly default: ComponentType<ViewProps<T>>; readonly Loading?: ComponentType }>;
type Entrance = () => Promise<{ readonly default: ComponentType<{ readonly page?: string; readonly account?: boolean }> }>;
interface PublicPlaces { readonly public: true; readonly pages: readonly { readonly id: string; readonly path: string }[] }

function modulePlace<K extends AnyViewName>(place: Place<K>, page: Page<ViewAnswer<K>>): Entrypoint {
  const read = moduleViewOf(place.view, place.route);
  const passed = (query: URLSearchParams) => Object.fromEntries(place.query.map((key) => [key, query.get(key) ?? undefined]));
  const facts = { title: place.title, path: [] };
  return entrypoint({ id: place.id, path: place.path, data: (_, query) => read({}, passed(query)), view: page, place: () => facts });
}

function publicPlaces(place: PublicPlaces, entrance: Entrance): Entrypoint[] {
  return place.pages.map(({ id, path }) => entrypoint({ id, path, public: true,
    view: () => entrance().then((loaded) => ({ default: () => createElement(loaded.default, { page: path }) })),
  }));
}

export function modulePlaces(): Entrypoint[] {
  const pages = new Map(filling<typeof later, "page", { readonly page: Page<never> }>(later, "page"));
  const screens = new Map(filling<typeof later, "screen", { readonly screen: Page<undefined> }>(later, "screen"));
  const entrances = new Map(filling<typeof later, "entrance", { readonly entrance: Entrance }>(later, "entrance"));
  type Spot = { readonly id: string; readonly path: string; readonly also?: readonly Spot[] };
  type Slot = { readonly place: Place<never> | PublicPlaces | Spot };
  return filling<typeof catalog | Record<string, Slot>, "place", Slot>(catalog, "place").flatMap(([name, { place }]) => {
    const entrance = entrances.get(name)?.entrance;
    if ("public" in place) return entrance === undefined ? [] : publicPlaces(place, entrance);
    const screen = screens.get(name)?.screen;
    if (!("view" in place)) {
      const spot: Spot = place;
      return screen === undefined ? [] : [spot, ...(spot.also ?? [])].map(({ id, path }) => entrypoint({ id, path, view: screen }));
    }
    const page = pages.get(name)?.page;
    return page === undefined ? [] : [modulePlace(place, page)];
  });
}
